import type { PostDto } from '@str/api/types';
import { Overlay } from '@/components/Overlay';
import {
  CHANNEL_META,
  CHANNEL_PROVIDER,
  CHANNELS,
  SCHEDULE_TIMES,
  propertyChannels,
  sceneBackground,
  splitCaption,
  type Channel,
  type PlanKey,
  type TikTokOptions,
  type YouTubeOptions,
  isoDateIn,
} from '@str/shared';
import { Link } from 'react-router';
import { errorMessage } from '@/lib/api';
import { useChannels, useTikTokCreator } from '@/lib/queries';
import { useModalDialog } from '@/lib/use-modal-dialog';
import { useState } from 'react';
import { ChannelLogo } from '@/components/ChannelLogo';
import { ErrorText } from '@/components/ui';
import './schedule-sheet.css';
import { TikTokPost } from '@/components/composer/TikTokPost';
import { YouTubePost } from '@/components/composer/YouTubePost';
import { addDays, DAYS, dayLabel, startOfWeek, toIsoDate, weekDays } from '@/lib/format';
import {
  firstOpenSlot,
  fromInputTime,
  hostTimeZone,
  isPreset,
  isDayOver,
  isSlotPast,
  openSlotOn,
  bestTimeCopy,
  parseBestTime,
  toInputTime,
  type BestTimeSource,
  type ScheduleTime,
} from '@/lib/schedule';
import { shiftDay } from '@str/shared';
import {
  EMPTY_TIKTOK_DRAFT,
  tiktokBlocker,
  tiktokOptionsFrom,
  type TikTokDraft,
} from '@/lib/tiktok-post';

type Props = {
  post: PostDto;
  version: {
    headline: string;
    scene: PostDto['scene'];
    warm: number;
    n: number;
    imageUrl?: string | null;
    coverUrl?: string | null;
    videoEdit?: {
      trim?: { startMs: number; endMs: number };
      layers?: { kind: string }[];
    } | null;
  } | null;
  format: string;
  /** The property's own tier (null = off the plan): which channels may be picked. */
  plan: PlanKey | null;
  channels: Channel[];
  toggleChannel: (c: Channel) => void;
  takenDates: Set<string>;
  bestTime: string;
  /**
   * Where `bestTime` came from. Today it is always picked from the listing (the plan task), not
   * from the account's results, so it reads as a good default until real audience data exists.
   */
  bestTimeSource?: BestTimeSource;
  initialDate: string;
  /** The slot the sheet opens on: the last post's when Home said make another like it. */
  initialTime?: ScheduleTime;
  /** The IANA zone the day and time are read in; the browser's unless told otherwise. */
  timezone?: string;
  busy: boolean;
  /** What the sheet is waiting on while busy (a rewrite of the words), or null. */
  busyText?: string | null;
  error: string | null;
  onClose: () => void;
  /** Saves the words the host changed on the TikTok panel, the same place the composer saves. */
  onCaption?: (body: string) => void;
  /** Saves the hashtags changed on the TikTok panel; without it they are shown, not edited. */
  onHashtags?: (hashtags: string[]) => void;
  onConfirm: (input: {
    date: string;
    time: ScheduleTime;
    publishNow: boolean;
    tiktok?: TikTokOptions;
    youtube?: YouTubeOptions;
  }) => void;
};

export function ScheduleSheet({
  post,
  version,
  format,
  plan,
  channels,
  toggleChannel,
  takenDates,
  bestTime,
  bestTimeSource = 'default',
  initialDate,
  initialTime = '6:30 PM',
  timezone,
  busy,
  busyText = null,
  error,
  onClose,
  onCaption,
  onHashtags,
  onConfirm,
}: Props) {
  const zone = timezone ?? hostTimeZone();
  // A post already on the calendar reopens on the day and time it is saved at, so saving it
  // untouched leaves it where it is (R14).
  const saved = post.scheduledDate && post.scheduledTime ? post : null;
  // A pick that has already gone by (a stale draft, a post scheduled in the past) opens on the
  // next slot still ahead; the slots themselves are greyed out below once they pass.
  const [start] = useState(() =>
    firstOpenSlot(saved?.scheduledDate ?? initialDate, saved?.scheduledTime ?? initialTime, zone),
  );
  const [date, setDate] = useState(start.date);
  const [time, setTime] = useState<ScheduleTime>(start.time);
  // The clock stays open once the host reached for it, even while the input is being typed.
  const [customOpen, setCustomOpen] = useState(() => !isPreset(start.time));
  const customGone = customOpen && slotPastNow(date, time);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(start.date + 'T00:00:00')));
  // Today on the property's clock, not the browser's: a host abroad still sees the right day.
  const todayIso = isoDateIn(new Date(), zone);
  const slotPast = (iso: string, t: ScheduleTime) => isSlotPast(iso, t, zone);
  function slotPastNow(iso: string, t: ScheduleTime) {
    return isSlotPast(iso, t, zone);
  }
  // Today stays open until its last minute on the property clock: a later custom time still fits.
  const dayGone = (iso: string) => isDayOver(iso, zone);
  const someGone = SCHEDULE_TIMES.some((t) => slotPast(date, t));
  const pickDay = (iso: string) => {
    setDate(iso);
    if (slotPast(iso, time)) {
      const open = openSlotOn(iso, time, zone);
      if (open.date === iso) {
        setTime(open.time);
        if (!isPreset(open.time)) setCustomOpen(true);
      }
    }
  };
  const week = weekDays(weekStart);
  const best = parseBestTime(bestTime);
  const bestThisWeek = toIsoDate(week[best.day]!);
  // This week's best slot may already be behind us; then the tip means next week's.
  const bestNextWeek = slotPast(bestThisWeek, best.time);
  const bestIso = bestNextWeek ? shiftDay(bestThisWeek, 7) : bestThisWeek;
  const showBestTip = !(date === bestIso && time === best.time);
  const bestCopy = bestTimeCopy(bestNextWeek ? `next ${bestTime}` : bestTime, bestTimeSource);
  const connected = useChannels(post.propertyId);
  const connectedChannels = new Set(
    (connected.data?.connections ?? [])
      .filter((c) => c.status !== 'disconnected')
      .map((c) => c.channel),
  );
  // A platform still reviewing our app cannot be connected, so saying "Connect" would be a dead end.
  const waiting = (connected.data?.providers ?? []).filter(
    (p) => p.unavailable && channels.includes(p.channel),
  );
  const missing = channels.filter(
    (c) => connected.data && !connectedChannels.has(c) && !waiting.some((p) => p.channel === c),
  );
  // The property's tier decides what can be picked at all; the rest are shown, locked.
  const allowed = propertyChannels(plan);
  // A channel still in review is locked for everyone: it is no reason to change plan.
  const inReview = new Map(
    (connected.data?.providers ?? []).flatMap((p) =>
      p.unavailable ? [[p.channel, p.unavailable] as const] : [],
    ),
  );
  const lockedChannels = CHANNELS.filter((c) => !allowed.includes(c) && !inReview.has(c));
  const story = post.format === 'Story';
  // TikTok requires the host to see their account and choose these themselves before a post.
  const tiktokConn = (connected.data?.connections ?? []).find(
    (c) => c.channel === 'TT' && c.status !== 'disconnected',
  );
  const wantsTikTok = channels.includes('TT');
  const creator = useTikTokCreator(wantsTikTok ? tiktokConn?.id : null);
  const [tiktok, setTiktok] = useState<TikTokDraft>(EMPTY_TIKTOK_DRAFT);
  // Taking TikTok off the post throws the confirmation away, so putting it back asks again.
  const [wasWanted, setWasWanted] = useState(wantsTikTok);
  if (wasWanted !== wantsTikTok) {
    setWasWanted(wantsTikTok);
    if (!wantsTikTok && tiktok !== EMPTY_TIKTOK_DRAFT) setTiktok(EMPTY_TIKTOK_DRAFT);
  }
  const [words, setWords] = useState(() => splitCaption(post.caption ?? '').body);
  const [tags, setTags] = useState(() =>
    splitCaption(post.caption ?? '')
      .hashtags.map((t) => `#${t}`)
      .join(' '),
  );
  const trim = version?.videoEdit?.trim;
  const clipSeconds = trim ? Math.max(0, trim.endMs - trim.startMs) / 1000 : null;
  const blocker = creator.data ? tiktokBlocker(tiktok, creator.data, clipSeconds) : null;
  const tiktokChoices =
    wantsTikTok && creator.data
      ? (tiktokOptionsFrom(tiktok, creator.data) ?? undefined)
      : undefined;
  // Not connected, it is only marked so. Connected, TikTok must have answered and the host must
  // have confirmed: nothing goes to TikTok on a guess, and the panel says how to take it off.
  const tiktokReady = !wantsTikTok || !tiktokConn || (!blocker && !!tiktokChoices);
  const tiktokHold = wantsTikTok && tiktokConn && !tiktokReady ? (blocker ?? undefined) : undefined;
  // YouTube's rules: the host sets the title and who can see the video, and sees the channel.
  const youtubeConn = (connected.data?.connections ?? []).find(
    (c) => c.channel === 'YT' && c.status !== 'disconnected',
  );
  const [youtubeTitle, setYoutubeTitle] = useState(version?.headline ?? post.title);
  const [youtubePrivacy, setYoutubePrivacy] = useState<YouTubeOptions['privacyStatus']>('public');
  const youtubeAsked = channels.includes('YT') && !!youtubeConn;
  const youtubeChoices: YouTubeOptions | undefined =
    youtubeAsked && youtubeTitle.trim()
      ? { privacyStatus: youtubePrivacy, title: youtubeTitle.trim() }
      : undefined;
  const youtubeReady = !youtubeAsked || !!youtubeChoices;
  const hold = tiktokHold ?? (youtubeReady ? undefined : 'Give the YouTube video a title.');
  // Instagram and Facebook shape the caption; adding one here rewrites the words for it.
  const rewriteFor = channels.filter(
    (c) => (c === 'IG' || c === 'FB') && !post.channels.includes(c) && !(story && c === 'FB'),
  );
  // What the publisher sends: the post's own link, in the words that go out. The goal says nothing;
  // the host can turn the link off on a bookings post, and an unlinked draft has no line to send.
  const carriesLink = !!post.trackedLink && !!splitCaption(post.caption ?? '').link;
  const summary = `${dayLabel(new Date(date + 'T00:00:00'))} · ${time} · ${channels.length ? channels.join(', ') : 'no channels'}${carriesLink ? ' · tracked link' : ''}`;
  const hasTextLayer = !!version?.videoEdit?.layers?.some((l) => l.kind === 'text');

  const [dialogRef, onDialogKeyDown] = useModalDialog(onClose);
  return (
    <Overlay onClose={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={format === 'Reel' ? 'Schedule this reel' : 'Schedule this post'}
        tabIndex={-1}
        ref={dialogRef}
        onKeyDown={onDialogKeyDown}
        onClick={(e) => e.stopPropagation()}
        className="schedule-sheet"
      >
        {version?.imageUrl ? (
          // The whole picture, as it will post: its words are painted on it, so no second headline.
          <div className="schedule-sheet-cover schedule-sheet-cover-picture hide-md">
            <img src={version.imageUrl} alt={version.headline} />
            {!hasTextLayer && (
              <div className="schedule-sheet-cover-meta">
                {format} · v{version.n}
              </div>
            )}
          </div>
        ) : (
          <div
            className="schedule-sheet-cover hide-md"
            style={{
              background: sceneBackground(version?.scene ?? post.scene, version?.warm ?? 0),
            }}
          >
            {/* A reel with the host's words on it shows them in its cover: no second title. */}
            {!hasTextLayer && (
              <>
                <div
                  className="serif"
                  style={{ fontSize: 22, lineHeight: 1.1, letterSpacing: '-.02em' }}
                >
                  {version?.headline ?? post.title}
                </div>
                <div style={{ marginTop: 8, fontSize: 12, opacity: 0.85 }}>
                  {format} · v{version?.n ?? 1}
                </div>
              </>
            )}
          </div>
        )}
        <div className="schedule-sheet-body">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-.025em' }}>
              {format === 'Reel' ? 'Schedule this reel' : 'Schedule this post'}
            </span>
            <button
              type="button"
              aria-label="Close"
              className="hv"
              onClick={onClose}
              style={{
                width: 30,
                height: 30,
                borderRadius: 99,
                background: '#f4f2ed',
                border: 0,
                color: '#555',
                fontSize: 14,
              }}
            >
              ✕
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="label-caps">Day</span>
              <span
                style={{
                  display: 'flex',
                  gap: 4,
                  alignItems: 'center',
                  fontSize: 12,
                  color: '#777',
                }}
              >
                <button
                  type="button"
                  aria-label="Previous week"
                  className="icon-btn hv"
                  style={{ width: 24, height: 24, fontSize: 12 }}
                  onClick={() => setWeekStart(addDays(weekStart, -7))}
                >
                  ‹
                </button>
                {week[0]!.toLocaleString('en-US', { month: 'short' })} {week[0]!.getDate()} –{' '}
                {week[6]!.getDate()}
                <button
                  type="button"
                  aria-label="Next week"
                  className="icon-btn hv"
                  style={{ width: 24, height: 24, fontSize: 12 }}
                  onClick={() => setWeekStart(addDays(weekStart, 7))}
                >
                  ›
                </button>
              </span>
            </div>
            <div className="schedule-sheet-days">
              {week.map((d, i) => {
                const iso = toIsoDate(d);
                const sel = iso === date;
                const taken = takenDates.has(iso);
                const past = iso < todayIso || dayGone(iso);
                return (
                  <button
                    key={iso}
                    type="button"
                    disabled={past}
                    aria-pressed={sel}
                    className="hv lift-1"
                    onClick={() => pickDay(iso)}
                    style={{
                      background: sel ? '#111' : '#fff',
                      color: sel ? '#fff' : taken || past ? '#bbb' : '#111',
                    }}
                  >
                    <span className="schedule-sheet-wd">{DAYS[i]}</span>{' '}
                    <span className="schedule-sheet-dn">{d.getDate()}</span>
                    <span
                      className="dot"
                      style={{
                        width: 6,
                        height: 6,
                        background:
                          i === best.day ? 'var(--accent)' : taken ? '#ddd' : 'transparent',
                      }}
                    />
                  </button>
                );
              })}
            </div>
            {showBestTip && (
              <button
                type="button"
                onClick={() => {
                  setDate(bestIso);
                  setCustomOpen(false);
                  setTime(best.time);
                  if (bestNextWeek) setWeekStart(addDays(weekStart, 7));
                }}
                className="schedule-sheet-best hv"
              >
                <span>
                  <span style={{ color: 'var(--accent)' }}>✦</span> {bestCopy.lead}{' '}
                  <b>{bestCopy.when}</b>.{bestCopy.tail ? ` ${bestCopy.tail}` : ''}
                </span>
                <span className="schedule-sheet-best-action">Use best time</span>
              </button>
            )}
          </div>
          <div className="schedule-sheet-when">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
              <span className="label-caps">Time</span>
              <div className="schedule-sheet-times">
                {SCHEDULE_TIMES.map((t) => {
                  const gone = slotPast(date, t);
                  return (
                    <button
                      key={t}
                      type="button"
                      aria-pressed={!customOpen && time === t}
                      disabled={gone}
                      className="hv"
                      onClick={() => {
                        setCustomOpen(false);
                        setTime(t);
                      }}
                      style={{
                        background: !customOpen && time === t ? '#111' : '#fff',
                        color: !customOpen && time === t ? '#fff' : gone ? '#bbb' : '#111',
                        textDecoration: gone ? 'line-through' : undefined,
                      }}
                    >
                      {t}
                    </button>
                  );
                })}
                <button
                  type="button"
                  aria-pressed={customOpen}
                  className="hv"
                  onClick={() => setCustomOpen(true)}
                  style={{
                    background: customOpen ? '#111' : '#fff',
                    color: customOpen ? '#fff' : '#111',
                  }}
                >
                  Pick a time
                </button>
              </div>
              {customOpen && (
                <input
                  type="time"
                  step={60}
                  aria-label="Custom time"
                  value={toInputTime(time)}
                  onChange={(e) => {
                    const t = fromInputTime(e.target.value);
                    if (t) setTime(t);
                  }}
                  style={{
                    font: 'inherit',
                    fontSize: 15,
                    padding: '9px 12px',
                    borderRadius: 10,
                    border: '1px solid rgba(0,0,0,.12)',
                  }}
                />
              )}
              {customGone && (
                <span style={{ fontSize: 11.5, color: '#a33a2a' }}>
                  That time has already passed today. Pick a later one.
                </span>
              )}
              <span style={{ fontSize: 11.5, color: '#999' }}>
                Times are in {zone.replace(/_/g, ' ')}.
              </span>
              {someGone && !customGone && (
                <span style={{ fontSize: 11.5, color: '#999' }}>
                  Earlier times today have passed.
                </span>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
              <span className="label-caps">Channels</span>
              <div style={{ display: 'flex', gap: 6 }}>
                {CHANNELS.map((c) => {
                  const locked = !allowed.includes(c);
                  const noStories = story && c === 'FB';
                  const on = channels.includes(c) && !noStories && !locked;
                  const linked = !connected.data || connectedChannels.has(c);
                  const review = locked ? inReview.get(c) : undefined;
                  return (
                    <button
                      key={c}
                      type="button"
                      aria-pressed={on}
                      aria-label={`${CHANNEL_META[c].label}${review ? ' (in review)' : locked ? ' (Pro)' : noStories ? ' (no stories)' : linked ? '' : ' (not connected)'}`}
                      title={
                        review
                          ? review
                          : locked
                            ? `${CHANNEL_META[c].label} · included in Pro`
                            : noStories
                              ? 'Facebook has no stories here'
                              : linked
                                ? CHANNEL_META[c].label
                                : `${CHANNEL_META[c].label} · not connected`
                      }
                      className="hv lift-1"
                      disabled={noStories || locked}
                      onClick={() => toggleChannel(c)}
                      style={{
                        width: 38,
                        height: 38,
                        borderRadius: 10,
                        background: '#fff',
                        border: on
                          ? `2px ${linked ? 'solid' : 'dashed'} ${CHANNEL_META[c].color}`
                          : `1px ${linked ? 'solid' : 'dashed'} rgba(0,0,0,.2)`,
                        display: 'grid',
                        placeItems: 'center',
                        opacity: linked ? 1 : 0.75,
                      }}
                    >
                      <ChannelLogo provider={CHANNEL_PROVIDER[c]} size={20} muted={!on} />
                    </button>
                  );
                })}
              </div>
              {lockedChannels.length > 0 && (
                <span style={{ fontSize: 11.5, color: '#666', lineHeight: 1.4 }}>
                  {lockedChannels.map((c) => CHANNEL_META[c].label).join(' and ')}{' '}
                  {lockedChannels.length > 1 ? 'are' : 'is'} included in Pro.{' '}
                  <Link to="/app/settings">Change plan</Link>
                </span>
              )}
              {story && (
                <span style={{ fontSize: 11.5, color: '#666', lineHeight: 1.4 }}>
                  A story is the picture alone on Instagram; the caption and link stay here.
                  Facebook has no stories, so it is left out.
                </span>
              )}
              {rewriteFor.length > 0 && (
                <span style={{ fontSize: 11.5, color: '#666', lineHeight: 1.4 }}>
                  This draft was not written for{' '}
                  {rewriteFor.map((c) => CHANNEL_META[c].label).join(' or ')}; the words are
                  rewritten for it when you schedule. The picture stays.
                </span>
              )}
              {channels.length > 0 && (
                <span style={{ fontSize: 11.5, color: '#555', lineHeight: 1.4 }}>
                  Posting as{' '}
                  {channels
                    .map((c) => {
                      const conn = (connected.data?.connections ?? []).find(
                        (k) => k.channel === c && k.status !== 'disconnected',
                      );
                      return conn
                        ? `${conn.handle ? `@${conn.handle}` : conn.displayName} (${CHANNEL_META[c].label})`
                        : null;
                    })
                    .filter(Boolean)
                    .join(', ') || 'no connected account yet'}
                </span>
              )}
              {waiting.map((p) => (
                <span key={p.key} style={{ fontSize: 11.5, color: '#666', lineHeight: 1.4 }}>
                  {p.unavailable}
                </span>
              ))}
              {missing.length > 0 && (
                <span style={{ fontSize: 11.5, color: '#a33a2a', lineHeight: 1.4 }}>
                  {missing.map((c) => CHANNEL_META[c].label).join(', ')} not connected yet — the
                  post is marked “not connected” there. <Link to="/app/channels">Connect</Link>
                </span>
              )}
            </div>
          </div>
          {wantsTikTok && tiktokConn && (
            <TikTokPost
              accountName={
                creator.data?.nickname ??
                creator.data?.username ??
                tiktokConn.displayName ??
                'your account'
              }
              creator={creator.data}
              loading={creator.isPending}
              failed={creator.isError}
              failedWhy={creator.error ? errorMessage(creator.error) : null}
              photoPost={post.format !== 'Reel' && post.format !== 'Story'}
              previewUrl={version?.coverUrl ?? version?.imageUrl ?? null}
              words={words}
              onWords={setWords}
              onWordsCommit={() => onCaption?.(words)}
              hashtags={tags}
              onHashtags={onHashtags ? setTags : undefined}
              onHashtagsCommit={() => onHashtags?.(tags.split(/[#\s,]+/).filter(Boolean))}
              linkLeftOut={!!splitCaption(post.caption ?? '').link}
              draft={tiktok}
              onDraft={setTiktok}
              blocker={blocker}
            />
          )}
          {youtubeAsked && youtubeConn && (
            <YouTubePost
              accountName={youtubeConn.displayName}
              title={youtubeTitle}
              onTitle={setYoutubeTitle}
              words={words}
              onWords={setWords}
              onWordsCommit={() => onCaption?.(words)}
              privacy={youtubePrivacy}
              onPrivacy={setYoutubePrivacy}
            />
          )}
          <ErrorText>{error}</ErrorText>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <span style={{ fontSize: 12.5, color: '#999' }}>{summary}</span>
            <div className="schedule-sheet-actions">
              <button
                type="button"
                className="btn btn-outline hv"
                disabled={busy || !channels.length || !tiktokReady || !youtubeReady}
                title={hold}
                onClick={() =>
                  onConfirm({
                    date,
                    time,
                    publishNow: true,
                    tiktok: tiktokChoices,
                    youtube: youtubeChoices,
                  })
                }
              >
                Post now
              </button>
              <button
                type="button"
                className="btn btn-primary hv"
                disabled={busy || !channels.length || !tiktokReady || !youtubeReady || customGone}
                title={hold}
                onClick={() =>
                  onConfirm({
                    date,
                    time,
                    publishNow: false,
                    tiktok: tiktokChoices,
                    youtube: youtubeChoices,
                  })
                }
              >
                {busy ? (busyText ?? 'Scheduling…') : 'Add to calendar'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Overlay>
  );
}
